import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';

const CustomizedCategories = () => {
  const [categoriesByType, setCategoriesByType] = useState({});
  const [newCategory, setNewCategory] = useState('');
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [newExpenseType, setNewExpenseType] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');

  // Default expense types that cannot be edited or deleted
  const fixedExpenseTypes = [
    'Materials', 'Overhead', 'Travel', 'Fee', 'Equipment',
    'Sub-contract', 'Internal Bank Transfer', 'Credit Card Payment',
    'N/A (Non-grant Item)'
  ];

  useEffect(() => {
    fetchCategoriesByType();
    fetchExpenseTypes();
  }, []);

  const fetchCategoriesByType = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/by-expense-type`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setCategoriesByType(data);
    } catch (error) {
      console.error('Error fetching categories by type:', error);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      // Filter out fixed types so they can't be edited/deleted (case-insensitive)
      const fixedLower = fixedExpenseTypes.map(f => f.toLowerCase());
      setExpenseTypes(data.filter(t => !fixedLower.includes(t.toLowerCase())));
    } catch (error) {
      console.error('Error fetching expense types:', error);
    }
  };

  // Sub-category being added: the category it belongs to, plus the typed name.
  const [addingSubcatFor, setAddingSubcatFor] = useState(null);
  // Sub-category being renamed: { type, name } of the original.
  const [editingSubcat, setEditingSubcat] = useState(null);
  const [editSubcatValue, setEditSubcatValue] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  const handleAddCategory = async (expenseType) => {
    if (!newCategory.trim()) return;
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories`, {
        category: newCategory,
        expenseType
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      setNewCategory('');
      setAddingSubcatFor(null);
      fetchCategoriesByType();
    } catch (error) {
      console.error('Error adding customized category:', error);
      alert(error.message);
    }
  };

  const handleRenameCategory = async () => {
    if (!editingSubcat) return;
    const next = editSubcatValue.trim();
    if (!next || next === editingSubcat.name) {
      setEditingSubcat(null);
      return;
    }
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/${encodeURIComponent(editingSubcat.name)}`,
        { newCategory: next, expenseType: editingSubcat.type }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      setEditingSubcat(null);
      fetchCategoriesByType();
    } catch (error) {
      console.error('Error renaming customized category:', error);
      alert(error.message);
    }
  };

  const handleDeleteCategory = async (category, expenseType) => {
    if (!window.confirm(`Remove the sub-category "${category}"?`)) return;
    try {
      const url = `${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/${encodeURIComponent(category)}${expenseType ? `?expenseType=${encodeURIComponent(expenseType)}` : ''}`;
      const response = await api.delete(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      fetchCategoriesByType();
    } catch (error) {
      console.error('Error deleting customized category:', error);
      alert(error.message);
    }
  };

  const handleAddExpenseType = async () => {
    if (!newExpenseType.trim()) return;
    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types`, { type: newExpenseType });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      await response.json();
      setNewExpenseType('');
      setShowAddCategory(false);
      fetchExpenseTypes();
    } catch (error) {
      console.error('Error adding expense type:', error);
      alert(error.message);
    }
  };

  const handleDeleteExpenseType = async (type) => {
    if (!window.confirm(`Remove the category "${type}"?`)) return;
    try {
      const response = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types/${encodeURIComponent(type)}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      await response.json();
      fetchExpenseTypes();
    } catch (error) {
      console.error('Error deleting expense type:', error);
      alert(error.message);
    }
  };

  const handleEditExpenseType = async (oldType) => {
    if (!editTypeValue.trim() || editTypeValue.trim() === oldType) {
      setEditingType(null);
      return;
    }
    try {
      const response = await api.put(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories/expense-types/${encodeURIComponent(oldType)}`, { newType: editTypeValue });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      await response.json();
      setEditingType(null);
      fetchExpenseTypes();
      fetchCategoriesByType(); // Refresh subcategories as expense type name changed
    } catch (error) {
      console.error('Error editing expense type:', error);
      alert(error.message);
    }
  };

  const iconBtn = 'p-1 text-gray-400 rounded transition-colors';
  const EditIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
  const TrashIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );

  // Every category (system + custom) in one list, each with its sub-categories
  // nested underneath.
  const allCategories = [
    ...expenseTypes.map(type => ({ type, isFixed: false })),
    ...fixedExpenseTypes.map(type => ({ type, isFixed: true }))
  ].filter((entry, i, arr) => arr.findIndex(e => e.type.toLowerCase() === entry.type.toLowerCase()) === i);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/grant-fund-financials?tab=unconfirmed" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Bank Statements
          </Link>
        </div>

        <div className="card p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Expense Type / Subcategory</h1>
              <p className="text-gray-600 text-sm mt-1">
                Manage expense types (categories) and their sub-categories. When users pick a category
                in the transaction table, only its sub-categories appear.
              </p>
            </div>
            <button
              onClick={() => setShowAddCategory(v => !v)}
              className="shrink-0 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Add Category
            </button>
          </div>

          {showAddCategory && (
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                value={newExpenseType}
                onChange={(e) => setNewExpenseType(e.target.value)}
                placeholder="New category name"
                className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddExpenseType(); if (e.key === 'Escape') setShowAddCategory(false); }}
                autoFocus
              />
              <button onClick={handleAddExpenseType} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Add</button>
              <button onClick={() => { setShowAddCategory(false); setNewExpenseType(''); }} className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Cancel</button>
            </div>
          )}
        </div>

        <div className="card p-6 space-y-3">
          {allCategories.map(({ type, isFixed }) => {
            const typeSubcats = categoriesByType[type] || [];
            return (
              <div key={type} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Category row */}
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between gap-2">
                  {editingType === type ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editTypeValue}
                        onChange={(e) => setEditTypeValue(e.target.value)}
                        className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleEditExpenseType(type); if (e.key === 'Escape') setEditingType(null); }}
                        autoFocus
                      />
                      <button onClick={() => handleEditExpenseType(type)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                      <button onClick={() => setEditingType(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-800">{type}</h3>
                      {isFixed && <span className="text-xs text-gray-400">System</span>}
                      <span className="text-xs text-gray-500">
                        ({typeSubcats.length} sub-categor{typeSubcats.length === 1 ? 'y' : 'ies'})
                      </span>
                    </div>
                  )}
                  {editingType !== type && (
                    <div className="flex items-center gap-1">
                      {!isFixed && (
                        <>
                          <button
                            onClick={() => { setEditingType(type); setEditTypeValue(type); }}
                            className={`${iconBtn} hover:text-blue-600`}
                            title="Rename category"
                          >
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => handleDeleteExpenseType(type)}
                            className={`${iconBtn} hover:text-red-600`}
                            title="Remove category"
                          >
                            <TrashIcon />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => { setAddingSubcatFor(type); setNewCategory(''); }}
                        className="ml-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
                      >
                        + Add Sub-category
                      </button>
                    </div>
                  )}
                </div>

                {/* Sub-categories, indented under their category */}
                {(typeSubcats.length > 0 || addingSubcatFor === type) && (
                  <div className="py-2 pl-8 pr-4 space-y-1">
                    {typeSubcats.map((subcat) => (
                      <div key={`${type}-${subcat}`} className="flex items-center justify-between px-3 py-1.5 rounded bg-white border border-gray-100">
                        {editingSubcat && editingSubcat.type === type && editingSubcat.name === subcat ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editSubcatValue}
                              onChange={(e) => setEditSubcatValue(e.target.value)}
                              className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCategory(); if (e.key === 'Escape') setEditingSubcat(null); }}
                              autoFocus
                            />
                            <button onClick={handleRenameCategory} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                            <button onClick={() => setEditingSubcat(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                          </div>
                        ) : (
                          <>
                            <span className="text-sm text-gray-700">{subcat}</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setEditingSubcat({ type, name: subcat }); setEditSubcatValue(subcat); }}
                                className={`${iconBtn} hover:text-blue-600`}
                                title="Rename sub-category"
                              >
                                <EditIcon />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(subcat, type)}
                                className={`${iconBtn} hover:text-red-600`}
                                title="Remove sub-category"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {addingSubcatFor === type && (
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <input
                          type="text"
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                          placeholder="Sub-category name"
                          className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(type); if (e.key === 'Escape') setAddingSubcatFor(null); }}
                          autoFocus
                        />
                        <button onClick={() => handleAddCategory(type)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                        <button onClick={() => setAddingSubcatFor(null)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {allCategories.length === 0 && (
            <p className="text-sm text-gray-500 py-4 text-center">No categories configured</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizedCategories;
